import React, { FC, useEffect, useState } from 'react';
import { FigureFile, getImageFiguresWithInstances, ImageFigure, updateImageFigureInEditor } from './figureHelper';
import { useGuideCmsContext } from '../GuideCmsContext';
import { GuideFigureDialogProps, GuideFigureDialogResult } from './GuideFigureDialog';
import { Modal } from '../../../dialogs/useModal';

interface FiguresContext {
  addFile: (file: FigureFile) => void;
  deleteFile: (fileId: string) => void;
  closeModal: () => void;
  confirmAndClose: () => void;
  editingFigure?: ImageFigure;
}

const FiguresContextInstance = React.createContext<FiguresContext | undefined>(undefined);

export const useFiguresContext = (): FiguresContext => {
  const context = React.useContext(FiguresContextInstance);
  if (!context) {
    throw new Error('useFiguresContext must be used within a FiguresContextProvider');
  }
  return context;
};

export const FiguresContextProvider: FC<{ modal: Modal<GuideFigureDialogProps, GuideFigureDialogResult>; children: React.ReactNode }> = ({
  modal,
  children,
}) => {
  const { editor } = useGuideCmsContext();
  const [editingFigure, setEditingFigure] = useState<ImageFigure>();

  useEffect(() => {
    if (!editor || !modal.data.editFigureId) return;
    const figures = getImageFiguresWithInstances(editor);
    const found = figures.find((f) => f.figureId === modal.data.editFigureId);
    setEditingFigure(found ?? { figureId: modal.data.editFigureId, files: [] });
  }, [editor, modal.data.editFigureId]);

  const addFile = (file: FigureFile) => {
    if (!editingFigure || !editor) return;
    const newFiles = [...editingFigure.files, file];
    const newImageFigure = { ...editingFigure, files: newFiles };
    setEditingFigure(newImageFigure);
    if (!modal.data.isNew) {
      updateImageFigureInEditor(editor, newImageFigure);
    }
  };

  const deleteFile = (fileId: string) => {
    if (!editingFigure || !editor) return;
    const newFiles = editingFigure.files.filter((f) => f.id !== fileId);
    const newImageFigure = { ...editingFigure, files: newFiles };
    setEditingFigure(newImageFigure);
    if (!modal.data.isNew) {
      updateImageFigureInEditor(editor, newImageFigure);
    }
  };

  return (
    <FiguresContextInstance.Provider
      value={{
        addFile,
        deleteFile,
        closeModal: () => modal.close({}),
        confirmAndClose: () => modal.close({ figure: editingFigure }),
        editingFigure,
      }}>
      {children}
    </FiguresContextInstance.Provider>
  );
};
